import React, { useId, useRef } from 'react';
import PropTypes from 'prop-types';
import './ui.css';

/**
 * Tabs with the WAI-ARIA tab pattern: role=tablist, roving tabindex (only the
 * selected tab is in the Tab order), ArrowLeft/ArrowRight (wrapping), Home and
 * End move focus and selection together. A tab with `keepMounted` keeps its
 * panel in the DOM (hidden) when another tab is selected, so its state (a
 * generated brief, a chat) survives switching away and back.
 */
export function Tabs({
    label, tabs, value, onChange, className, panelClassName,
}) {
    const base = useId().replace(/:/g, '');
    const refs = useRef(new Map());
    const index = Math.max(0, tabs.findIndex((t) => t.id === value));
    const tabId = (id) => `${base}-tab-${id}`;
    const panelId = (id) => `${base}-panel-${id}`;

    const select = (i) => {
        const t = tabs[(i + tabs.length) % tabs.length];
        refs.current.get(t.id)?.focus();
        if (t.id !== value) onChange(t.id);
    };

    const onKeyDown = (e) => {
        const moves = {
            ArrowRight: () => select(index + 1),
            ArrowLeft: () => select(index - 1),
            Home: () => select(0),
            End: () => select(tabs.length - 1),
        };
        const move = moves[e.key];
        if (!move) return;
        e.preventDefault();
        move();
    };

    const active = tabs[index];
    const panelFor = (t) => (
        <div
            key={t.id}
            role="tabpanel"
            id={panelId(t.id)}
            aria-labelledby={tabId(t.id)}
            tabIndex={0}
            className={['v2-tabpanel', panelClassName].filter(Boolean).join(' ')}
            hidden={t.id !== active?.id}
        >
            {t.panel}
        </div>
    );
    return (
        <div className={['v2-tabs-wrap', className].filter(Boolean).join(' ')}>
            <div className="v2-tabs" role="tablist" aria-label={label}>
                {tabs.map((t) => {
                    const selected = t.id === active?.id;
                    return (
                        <button
                            key={t.id}
                            ref={(el) => { if (el) refs.current.set(t.id, el); else refs.current.delete(t.id); }}
                            type="button"
                            role="tab"
                            id={tabId(t.id)}
                            className="v2-tab"
                            aria-selected={selected}
                            aria-controls={panelId(t.id)}
                            tabIndex={selected ? 0 : -1}
                            onClick={() => { if (!selected) onChange(t.id); }}
                            onKeyDown={onKeyDown}
                        >
                            {t.label}
                            {t.count !== undefined && t.count !== null && <span className="v2-tab-ct">{t.count}</span>}
                        </button>
                    );
                })}
            </div>
            {/* The active panel, plus any `keepMounted` panel (hidden while
                inactive), whose state must survive a tab switch. */}
            {tabs.map((t) => {
                if (t.panel === undefined) return null;
                if (t.id === active?.id || t.keepMounted) return panelFor(t);
                return null;
            })}
        </div>
    );
}

Tabs.propTypes = {
    label: PropTypes.string.isRequired,
    tabs: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        label: PropTypes.node.isRequired,
        count: PropTypes.node,
        panel: PropTypes.node,
        // Keep the panel mounted (hidden) while another tab is shown.
        keepMounted: PropTypes.bool,
    })).isRequired,
    value: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    className: PropTypes.string,
    panelClassName: PropTypes.string,
};
